// src/pages/Exams/generate/SaveBar.tsx
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { JobRow, QuestionDraft, SaveResponse } from './types';
import { NAME_MAX } from '../../../school_panel/lib/fieldClass';
import { Check } from 'lucide-react';
import { Button } from '../../../school_panel/components/ui';
import { GEN_INPUT } from './MoreOptions';

interface Props {
  job: JobRow;
  questions: QuestionDraft[];
  onSave: (name: string) => Promise<SaveResponse>;
  error: string | null;
  conflict: boolean;
}

export function SaveBar({ job, questions, onSave, error, conflict }: Props) {
  const [name, setName] = useState(job.blueprint?.name ?? '');
  const [saving, setSaving] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    if (job.blueprint?.name && !name) setName(job.blueprint.name);
  }, [job.blueprint?.name, name]);

  const numForms = new Set(questions.map(q => q.form_label || 'A')).size || 1;
  const canSave = name.trim().length >= 3 && questions.length > 0;
  const label = numForms > 1
    ? `Save ${numForms} exams as set`
    : 'Save as exam';

  const submit = async () => {
    setSaving(true);
    try {
      const res = await onSave(name.trim());
      nav(`/exams/exam-set/${res.exam_set_id}`);
    } catch {
      // error state handled by hook
    } finally {
      setSaving(false);
    }
  };

  const kept = questions.length;
  const title = numForms > 1
    ? `Save ${kept} questions as ${numForms} exams`
    : `Save ${kept} question${kept === 1 ? '' : 's'} as an exam`;

  return (
    <div role="region" aria-label="Save exam" className="sticky bottom-4 z-[5] mt-[18px] rounded-[18px] bg-navy py-3.5 pl-5 pr-4 text-white shadow-actbar">
      <div className="flex flex-wrap items-center gap-3.5">
        <span className="mr-auto flex flex-col gap-0.5">
          <span className="font-display text-base font-bold">{title}</span>
          <span className="text-[13px] text-on-navy-muted">Nothing is shared until you assign it</span>
        </span>
        <div className="min-w-[240px] max-w-[420px] flex-[1_1_260px]">
          <label htmlFor="save-exam-name" className="sr-only">Exam name</label>
          <input
            id="save-exam-name"
            className={GEN_INPUT}
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={NAME_MAX}
            placeholder="e.g. Chemistry mid-term"
          />
        </div>
        <Button
          onClick={submit}
          disabled={!canSave || saving}
          loading={saving}
          icon={<Check size={18} strokeWidth={1.8} aria-hidden />}
          className="justify-center">
          {label}
        </Button>
      </div>
      {!canSave && (
        <p className="mt-2 text-xs text-on-navy-muted">
          {questions.length === 0
            ? 'There are no questions to save.'
            : 'Enter an exam name of at least 3 characters.'}
        </p>
      )}
      {(error || conflict) && (
        <div role="alert" className="mt-2 flex flex-wrap items-center gap-2 rounded-[12px] bg-danger-bg px-3 py-2 text-sm text-danger-ink">
          <span>{error}</span>
          {conflict && (
            <Button variant="ghost" size="sm" onClick={() => window.location.reload()}>
              Reload review
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
